import { getAppUrl } from '@/config/marketing-nav.config'

const signupHref = getAppUrl('/signup')
const portfolioHref = getAppUrl('/signup?next=/subscribe/portfolio')
const proHref = getAppUrl('/signup?next=/subscribe/pro')

export type PricingFeature = {
  label: string
  included: boolean
}

export type PricingFeatureSection = {
  title: string
  features: PricingFeature[]
}

export type PricingTier = {
  badge: string
  badgeVariant: 'default' | 'popular' | 'advanced'
  name: string
  price: string
  period?: string
  description: string
  ctaLabel: string
  ctaHref: string
  highlighted?: boolean
  sections: PricingFeatureSection[]
}

export type PricingValueProp = {
  title: string
  description: string
}

export const pricingContent = {
  eyebrow: 'Pricing',
  heroTitle: 'Three plans, one simple ladder',
  heroLead: 'Compare every feature across Free, Ivy Portfolio, and Ivy Pro.',
  tiers: [
    {
      badge: 'FREE',
      badgeVariant: 'default',
      name: 'Free',
      price: '$0',
      description: 'Essential tools to get started.',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
      sections: [
        {
          title: 'Ivy Chat',
          features: [
            { label: 'Ivy chat', included: true },
            {
              label: 'Additional monthly AI usage Starter usage only',
              included: true,
            },
            {
              label:
                'Real-time prices, SEC filings, market news & analyst ratings',
              included: true,
            },
            { label: 'Personalized chat experience', included: true },
            {
              label: 'Ivy Memory: remembers how you invest across chats.',
              included: true,
            },
          ],
        },
        {
          title: 'Market Data & Discovery',
          features: [
            { label: 'Intraday price action scanner', included: true },
            { label: 'Directional filtering', included: true },
            { label: 'Watchlists', included: true },
            { label: 'AI-powered charting and visualizations', included: true },
            { label: 'Key company stats & financials', included: true },
            { label: 'Real-time, full options chain', included: false },
            { label: '7-part company Snapshot', included: true },
          ],
        },
        {
          title: 'Portfolio & Brokerage',
          features: [
            {
              label: 'Up to 5 Ivy Baskets (portfolio simulator)',
              included: true,
            },
            {
              label: 'Brokerage connections 1 account · 30-day trial',
              included: true,
            },
            { label: 'Single risk profile calibration', included: true },
            { label: 'Live portfolio view 30-day trial', included: true },
            {
              label: 'Live P&L and portfolio reporting 30-day trial',
              included: true,
            },
            {
              label: 'Portfolio-driven strategy prompts 30-day trial',
              included: true,
            },
            { label: 'Multi-account intelligence', included: false },
          ],
        },
        {
          title: 'Daily Brief',
          features: [
            { label: 'General Daily Market Brief', included: true },
            { label: 'Personalized Daily Market Brief', included: false },
          ],
        },
        {
          title: 'Research Agents',
          features: [
            { label: 'Earnings Analyst', included: false },
            { label: 'Fundamental Valuation', included: false },
            { label: 'Sentiment & Narrative', included: false },
            { label: 'Risk Monitor', included: false },
            { label: 'Competitive & Strategy', included: false },
          ],
        },
      ],
    },
    {
      badge: 'MOST POPULAR',
      badgeVariant: 'popular',
      name: 'Ivy Portfolio',
      price: '$19',
      period: '/mo',
      description: 'Complete portfolio awareness with full personalization',
      ctaLabel: 'Start Your 7-Day Trial',
      ctaHref: portfolioHref,
      highlighted: true,
      sections: [
        {
          title: 'Ivy Chat',
          features: [
            { label: 'Ivy chat', included: true },
            {
              label: 'Additional monthly AI usage 2.5× more usage',
              included: true,
            },
            {
              label:
                'Real-time prices, SEC filings, market news & analyst ratings',
              included: true,
            },
            { label: 'Personalized chat experience', included: true },
            {
              label: 'Ivy Memory: remembers how you invest across chats.',
              included: true,
            },
          ],
        },
        {
          title: 'Market Data & Discovery',
          features: [
            { label: 'Intraday price action scanner', included: true },
            { label: 'Directional filtering', included: true },
            { label: 'Watchlists', included: true },
            { label: 'AI-powered charting and visualizations', included: true },
            { label: 'Key company stats & financials', included: true },
            { label: 'Real-time, full options chain', included: true },
            { label: '7-part company Snapshot', included: true },
          ],
        },
        {
          title: 'Portfolio & Brokerage',
          features: [
            {
              label: 'Up to 10 Ivy Baskets (portfolio simulator)',
              included: true,
            },
            { label: 'Brokerage connections Up to 5 accounts', included: true },
            { label: 'Multi account risk profile calibration', included: true },
            { label: 'Live portfolio view', included: true },
            { label: 'Live P&L and portfolio reporting', included: true },
            { label: 'Portfolio-driven strategy prompts', included: true },
            { label: 'Multi-account intelligence', included: true },
          ],
        },
        {
          title: 'Daily Brief',
          features: [
            { label: 'General Daily Market Brief', included: true },
            { label: 'Personalized Daily Market Brief', included: true },
          ],
        },
        {
          title: 'Research Agents',
          features: [
            { label: 'Earnings Analyst', included: false },
            { label: 'Fundamental Valuation', included: false },
            { label: 'Sentiment & Narrative', included: false },
            { label: 'Risk Monitor', included: false },
            { label: 'Competitive & Strategy', included: false },
          ],
        },
      ],
    },
    {
      badge: 'MOST ADVANCED',
      badgeVariant: 'advanced',
      name: 'Ivy Pro',
      price: '$59',
      period: '/mo',
      description: '10× your usage with the full research-agent suite.',
      ctaLabel: 'Sign up for Pro',
      ctaHref: proHref,
      sections: [
        {
          title: 'Ivy Chat',
          features: [
            { label: 'Ivy chat', included: true },
            {
              label: 'Additional monthly AI usage 10× more usage',
              included: true,
            },
            {
              label:
                'Real-time prices, SEC filings, market news & analyst ratings',
              included: true,
            },
            { label: 'Personalized chat experience', included: true },
            {
              label: 'Ivy Memory: remembers how you invest across chats.',
              included: true,
            },
          ],
        },
        {
          title: 'Market Data & Discovery',
          features: [
            { label: 'Intraday price action scanner', included: true },
            { label: 'Directional filtering', included: true },
            { label: 'Watchlists', included: true },
            { label: 'AI-powered charting and visualizations', included: true },
            { label: 'Key company stats & financials', included: true },
            { label: 'Real-time, full options chain', included: true },
            { label: '7-part company Snapshot', included: true },
          ],
        },
        {
          title: 'Portfolio & Brokerage',
          features: [
            {
              label: 'Up to 20 Ivy Baskets (portfolio simulator)',
              included: true,
            },
            {
              label: 'Brokerage connections Up to 10 accounts',
              included: true,
            },
            { label: 'Multi account risk profile calibration', included: true },
            { label: 'Live portfolio view', included: true },
            { label: 'Live P&L and portfolio reporting', included: true },
            { label: 'Portfolio-driven strategy prompts', included: true },
            { label: 'Multi-account intelligence', included: true },
          ],
        },
        {
          title: 'Daily Brief',
          features: [
            { label: 'General Daily Market Brief', included: true },
            { label: 'Personalized Daily Market Brief', included: true },
          ],
        },
        {
          title: 'Research Agents',
          features: [
            { label: 'Earnings Analyst', included: true },
            { label: 'Fundamental Valuation', included: true },
            { label: 'Sentiment & Narrative', included: true },
            { label: 'Risk Monitor', included: true },
            { label: 'Competitive & Strategy', included: true },
          ],
        },
      ],
    },
  ] satisfies PricingTier[],
  valuePropsEyebrow: 'What Every Plan Gets You',
  valuePropsTitle: 'Built around your portfolio — not a generic chatbot',
  valuePropsLead:
    'Every Ivy plan is wired into your brokerage, the live market, and a workspace that actually fits how active traders think.',
  valueProps: [
    {
      title: 'Portfolio-aware from day one',
      description:
        'Connect your brokerage and every answer is shaped by what you actually hold, trade, and watch — not a generic market summary.',
    },
    {
      title: 'One workspace, every workflow',
      description:
        'Charts, scans, options, news, and research agents all live in the same conversation. No tab-hopping, no copy-paste.',
    },
    {
      title: 'No lock-in, ever',
      description:
        'Month-to-month pricing with a 7-day free trial on paid plans. Switch between Free, Portfolio, and Pro any time your needs change.',
    },
  ] satisfies PricingValueProp[],
  faqEyebrow: 'Pricing FAQ',
  faqTitle: 'Questions, answered',
  faqLead:
    'The most common things people ask before signing up. Anything else - please visit the',
  faqLeadLink: {
    label: 'Help Center',
    href: 'https://help.podiummarkets.ai/',
  },
  faqItems: [
    {
      question: 'Is there a free trial?',
      answer:
        'Yes. There is a seven-day free trial for the Ivy Portfolio subscription. The free plan never expires. No credit card required to try the free plan.',
    },
    {
      question: 'Can I cancel anytime?',
      answer:
        'Yes. Our subscriptions are month-to-month. You can cancel your subscription(s) anytime. Access continues through the end of your billing period.',
    },
    {
      question: 'Can I switch plans later?',
      answer:
        'Yes. Switch between Free, Portfolio, and Pro any time your needs change.',
    },
    {
      question: 'How many Ivy Baskets can I create?',
      answer:
        'Free includes up to 5 Ivy Baskets, Ivy Portfolio includes up to 10, and Ivy Pro includes up to 20.',
    },
    {
      question: 'How do brokerage connections work?',
      answer:
        'Connect an account once and every Ivy answer is shaped by what you actually hold. Ivy pulls positions, cash, and options exposure from every connected brokerage into a single live view.',
    },
    {
      question: 'Do you offer annual pricing?',
      answer:
        'Not at this time — Ivy Portfolio and Ivy Pro are billed monthly. Annual options will appear on the Pricing page when available.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'Major credit and debit cards as well as Amazon Pay. Your card must be valid and not expired, have sufficient funds or credit available, allow for recurring/subscription charges and not be restricted for online purchases.',
    },
  ],
}
